/*
 * CAPTURE-OWNED MODULE
 * Guided setup v2 — pure decision logic behind the proposal review panel.
 *
 * Lives here for the same reason typeCorrection.js and classificationEvidence.js
 * do: capture-ui has no RTL, so every branch a test must pin is a plain function
 * with no I/O. The network calls stay in services/api.js and the fetching stays
 * on the page.
 *
 * THE PROPOSAL IS SERVER-AUTHORED AND THIS MODULE NEVER REBUILDS IT. It reads a
 * stored proposal for rendering and assembles a bounded EDIT payload — phrases,
 * ambiguity resolutions, ops to skip — which the server re-validates against the
 * live vocabularies before writing anything. Nothing computed here is trusted by
 * the server, and nothing here should ever try to be.
 */

/** An op that DEFINES a field, as opposed to binding one. */
const DEFINITION_OPS = ['reuse_field', 'create_field'];

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

/** Split a comma-separated keyword box the way the server normalises. */
export function parsePhrases(input) {
  if (Array.isArray(input)) {
    return [...new Set(input.filter((p) => typeof p === 'string').map((p) => p.trim().toLowerCase()).filter(Boolean))];
  }
  if (typeof input !== 'string') return [];
  return [...new Set(input.split(',').map((p) => p.trim().toLowerCase()).filter(Boolean))];
}

/**
 * Reduce a proposal's op list to what the panel renders.
 *
 * ONE ROW PER FIELD, NOT ONE PER OP. A field is two ops — its definition
 * (reuse_field / create_field) and its binding (associate_field) — because they
 * are two rows in two tables and slice 3 needs them separable to be replayable
 * on partial failure. That is an implementation fact of the dispatcher and
 * means nothing to a reviewer, who sees "Driver Name — new field" once. The
 * association's index still comes back on the row so a removal can skip both.
 *
 * Tolerates a proposal with no ops, a missing ops array, and an association
 * with no matching definition (which is what a hand-edited or partially applied
 * row can look like) — the panel must render something rather than throw.
 */
export function summariseOps(proposal) {
  const ops = asArray(proposal && proposal.ops);

  const typeOpIndex = ops.findIndex((o) => o && o.op === 'create_document_type');
  const typeOp = typeOpIndex === -1 ? null : ops[typeOpIndex];

  const documentType = typeOp
    ? {
        key: typeOp.key || '',
        label: typeOp.label || typeOp.key || '',
        phrases: asArray(typeOp.detection_phrases).filter((p) => typeof p === 'string'),
        rationale: typeof typeOp.rationale === 'string' ? typeOp.rationale : '',
        opIndex: typeOpIndex,
      }
    : null;

  // field_key → the index of the association that binds it, so a removal can
  // take both ops out in one action.
  const associationIndexByKey = new Map();
  ops.forEach((o, i) => {
    if (o && o.op === 'associate_field' && typeof o.field_key === 'string') {
      associationIndexByKey.set(o.field_key, i);
    }
  });

  const fields = [];
  ops.forEach((o, i) => {
    if (!o || !DEFINITION_OPS.includes(o.op)) return;
    // reuse_field names the field `field_key`; create_field names it `key`.
    // Normalising here is what lets the panel render one row shape.
    const key = o.op === 'reuse_field' ? o.field_key : o.key;
    if (typeof key !== 'string' || key === '') return;
    fields.push({
      op: o.op,
      key,
      label: o.label || key,
      field_type: o.field_type || 'text',
      wish: typeof o.wish === 'string' ? o.wish : '',
      reused: o.op === 'reuse_field',
      // Only ever true on a reuse. The panel says "currently disabled — will be
      // re-enabled", because reusing an inactive field silently yields one that
      // never appears in Library.
      tenant_fork_inactive: o.tenant_fork_inactive === true,
      rationale: typeof o.rationale === 'string' ? o.rationale : '',
      opIndex: i,
      associationOpIndex: associationIndexByKey.has(key) ? associationIndexByKey.get(key) : null,
    });
  });

  return { documentType, fields };
}

/**
 * Assemble the edit delta.
 *
 * EMPTY KEYS ARE OMITTED, not sent as empty values. The server treats a present
 * `detection_phrases` as "replace the stored array wholesale", so sending `[]`
 * for an untouched box would blank the proposal's own phrases and trip the hard
 * gate. Absent means "use what is stored"; present means "use exactly this".
 */
export function buildEdits({ phrases, resolutions, skipped } = {}) {
  const edits = {};

  const parsed = parsePhrases(phrases);
  if (parsed.length > 0) edits.detection_phrases = parsed;

  const entries = Object.entries(resolutions || {}).filter(([, v]) => v != null);
  if (entries.length > 0) edits.resolutions = Object.fromEntries(entries);

  const skipList = [...new Set(asArray(skipped).filter((n) => Number.isInteger(n) && n >= 0))].sort((a, b) => a - b);
  if (skipList.length > 0) edits.skip_ops = skipList;

  return edits;
}

/**
 * May the reviewer press "Create it"?
 *
 * Mirrors the server's own gate so the button state matches what apply will
 * accept: at least one non-empty phrase, and every ambiguity resolved. An
 * unresolved ambiguity is not merely incomplete — the server drops that wish
 * entirely, so applying would quietly create a type missing a field the
 * reviewer asked for.
 *
 * DELIBERATELY NOT CHECKING PHRASE LENGTH, and this now MATCHES the server.
 * The 2026-09-04 ruling is that the minimum-length floor does not apply to
 * hand-typed phrases — these come from a reviewer looking at the document, and
 * nine seeded system phrases (nda, sow, w-4, w4, irs, i-9, i9, ach, re:) are
 * below four characters and are the primary discriminators for their types.
 * applySetupProposal's gate is non-empty and nothing else, so "bol" enables the
 * button here and applies there.
 *
 * The floor still exists in buildSetupProposal, where it grades AI SUGGESTIONS
 * through the advisory phrases_sufficient flag — a different input with a
 * different failure mode, and not a rule this function should mirror.
 */
export function canApply({ phrases, ambiguities, resolutions } = {}) {
  if (parsePhrases(phrases).length === 0) return false;
  const unresolved = asArray(ambiguities).some((a) => {
    const wish = a && a.wish;
    if (typeof wish !== 'string') return false;
    return !(resolutions || {})[wish];
  });
  return !unresolved;
}
